import React from 'react';
import SecondaryBannerV2 from '@ajat/design-system/ContentBlocks/SecondaryBannerV2';
import { ComponentPage, DemoCard, ViewportFrame } from '../../shared';
import { EDITIONS_BANNER, BANNER_PHOTO } from './contentDemoData';

export const meta = {
  component: SecondaryBannerV2,
  componentName: 'SecondaryBannerV2',
  title: 'Secondary Banner V2',
  group: 'Content Blocks',
  importPath: '@ajat/design-system/ContentBlocks/SecondaryBannerV2',
  figmaNode: '2699:15510',
  description:
    'The editorial banner that opens a section (OVER-LL Editions): a breadcrumb, an H1 title and a short copy, centred in a 520px column. The surface is the --secondary-banner-v2-bg token (the Figma gradient, built from brand grays) unless `background` passes any CSS colour or gradient; `backgroundImage` lays a photo over it. `theme="dark"` turns the text light for a dark surface. Height follows the content (208px on desktop with the Figma copy). Without a title the block does not render; the breadcrumb and the copy appear when their data is passed.',
  props: [
    ['title', 'string', '—', 'Page title (typo-h1). Empty → the block is not rendered'],
    ['copy', 'node', '—', 'Description under the title (typo-body, gray-8)'],
    ['breadcrumbs', 'array', '[]', '[{ label, href?, onClick? }] — rendered with the DS Breadcrumbs; the last crumb is the current page; empty → hidden'],
    ['breadcrumbsLabel', 'string', "'Breadcrumb'", 'Accessible name of the breadcrumb nav — localise it'],
    ['background', 'string', '--secondary-banner-v2-bg', 'Any CSS colour or gradient, e.g. "var(--color-gray-075)"; also the colour under a loading photo'],
    ['backgroundImage', 'string', '—', 'Photo over the surface (desktop, and mobile unless backgroundImageMobile is set)'],
    ['backgroundImageMobile', 'string', '—', 'Mobile photo (< 1024px)'],
    ['theme', "'light' | 'dark'", "'light'", 'dark = light title / copy / breadcrumb for a dark colour or photo'],
    ['className', 'string', "''", 'Additional CSS class on the section'],
    ['style', 'object', '—', 'Inline style on the section (merged with background)'],
  ],
  usage: [
    {
      title: 'Section banner (Editions)',
      code: `import SecondaryBannerV2 from '@ajat/design-system/ContentBlocks/SecondaryBannerV2';

<SecondaryBannerV2
  breadcrumbs={[
    { label: 'HEM', href: '/', onClick: goTo('/') },
    { label: 'EDITIONS' },
  ]}
  breadcrumbsLabel="Brödsmulor"
  title="DESIGNA EDITIONS"
  copy="Våra Editions är färdigdesignade mallar skapade för att inspirera. …"
/>`,
    },
    {
      title: 'Colour, gradient or photo',
      code: `<SecondaryBannerV2 title="…" background="var(--color-gray-075)" />
<SecondaryBannerV2 title="…" background="linear-gradient(90deg, var(--color-gray-1), var(--color-gray-05))" />
<SecondaryBannerV2 title="…" backgroundImage={desktop} backgroundImageMobile={mobile} />
<SecondaryBannerV2 title="…" background="var(--color-gray-975)" theme="dark" />`,
    },
  ],
};

export default function SecondaryBannerV2Page() {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Gradient (Figma)" hint="the token default · Overall brand matches 2699:15510" flush>
        <SecondaryBannerV2 {...EDITIONS_BANNER} />
      </DemoCard>

      <DemoCard title="Mobile" hint="< 1024px · H1, spacing and gutters follow the mobile tokens" flush>
        <ViewportFrame width={390} height={260} label="SecondaryBannerV2 at 390px">
          <SecondaryBannerV2 {...EDITIONS_BANNER} />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Solid colour" hint='background="var(--color-gray-075)"' flush>
        <SecondaryBannerV2 {...EDITIONS_BANNER} background="var(--color-gray-075)" />
      </DemoCard>

      <DemoCard title="Photo" hint="backgroundImage · one photo for both widths" flush>
        <SecondaryBannerV2 {...EDITIONS_BANNER} backgroundImage={BANNER_PHOTO} />
      </DemoCard>

      <DemoCard title="Dark" hint='theme="dark" on background="var(--color-gray-975)"' flush>
        <SecondaryBannerV2 {...EDITIONS_BANNER} background="var(--color-gray-975)" theme="dark" />
      </DemoCard>

      <DemoCard title="Title only" hint="no breadcrumb, no copy" flush>
        <SecondaryBannerV2 title="DESIGNA EDITIONS" />
      </DemoCard>
    </ComponentPage>
  );
}
